import React from 'react';
import { Box } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import { Text } from '../Typography';
import { Section } from './signerStyles';
import { WetInkUploadOutcome } from './useWetInkSigner';

/** Inline red error callout, shared across the wet-ink surfaces. */
export const ErrorNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ backgroundColor: '#ffebee', padding: '12px 16px', borderRadius: '8px', mt: 2 }}>
    <Text small sx={{ color: '#c62828', wordBreak: 'break-word' }}>
      {children}
    </Text>
  </Box>
);

/**
 * Renders the verdict of a scan upload: clean, tampered, transient, or a plain guard error. The three
 * non-error kinds are visually distinct because they call for different user actions (guide §5).
 */
export const WetInkOutcome: React.FC<{ outcome: WetInkUploadOutcome }> = ({ outcome }) => (
  <Section>
    {outcome.kind === 'success' && (
      <Box sx={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '16px 20px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleOutlineIcon sx={{ color: '#16a34a', fontSize: 22 }} />
          <Text sx={{ fontWeight: 700, color: '#15803d' }}>
            {outcome.document.status === 'completed'
              ? 'Signed & document finalized'
              : 'Wet-ink signature recorded'}
          </Text>
        </Box>
        <Text small sx={{ color: '#166534', mt: 1, display: 'block' }}>
          {outcome.document.status === 'completed'
            ? 'This was the last required signer — the document auto-finalized.'
            : 'The scan passed the automated integrity check and was recorded as this signer’s signature.'}
        </Text>
      </Box>
    )}

    {outcome.kind === 'tampered' && (
      <Box sx={{ backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '12px', padding: '16px 20px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutlineIcon sx={{ color: '#c2410c', fontSize: 22 }} />
          <Text sx={{ fontWeight: 700, color: '#9a3412' }}>Verification failed — differences detected</Text>
        </Box>
        {outcome.summary && (
          <Text small sx={{ color: '#9a3412', mt: 1, display: 'block' }}>
            {outcome.summary}
          </Text>
        )}
        {outcome.discrepancies && outcome.discrepancies.length > 0 && (
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
            {outcome.discrepancies.map((d, i) => (
              <Box component="li" key={i} sx={{ color: '#9a3412', fontSize: '13px', lineHeight: '20px' }}>
                {d}
              </Box>
            ))}
          </Box>
        )}
        <Text small sx={{ color: '#7c2d12', mt: 1.5, display: 'block', fontWeight: 600 }}>
          Re-download the document (it may have changed), print, sign, and re-upload — don’t reuse an old copy.
        </Text>
      </Box>
    )}

    {outcome.kind === 'transient' && (
      <Box sx={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '16px 20px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReplayIcon sx={{ color: '#1d4ed8', fontSize: 22 }} />
          <Text sx={{ fontWeight: 700, color: '#1e40af' }}>Verification temporarily unavailable</Text>
        </Box>
        <Text small sx={{ color: '#1e40af', mt: 1, display: 'block' }}>
          {outcome.message} Nothing about your submission was rejected — retry the same upload shortly.
        </Text>
      </Box>
    )}

    {outcome.kind === 'error' && <ErrorNote>{outcome.message}</ErrorNote>}
  </Section>
);
